import { createParser, useQueryState } from 'nuqs'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'

const EncounterNavigationSourceSchema = z.discriminatedUnion('source', [
  z.object({ entityId: z.undefined().optional(), source: z.literal('list') }),
  z.object({
    entityId: z.undefined().optional(),
    source: z.literal('encounters'),
  }),
  z.object({
    entityId: z.undefined().optional(),
    source: z.literal('phone-queue'),
  }),
  z.object({ entityId: z.uuid(), source: z.literal('project') }),
  z.object({ entityId: z.uuid(), source: z.literal('patient') }),
  z.object({ entityId: z.uuid(), source: z.literal('case') }),
  z.object({
    entityId: z.uuid().optional(),
    source: z.literal('block'),
  }),
  z.object({ entityId: z.uuid(), source: z.literal('request') }),
  z.object({ entityId: z.undefined().optional(), source: z.literal('phone') }),
])

export type EncounterNavigationSource = z.infer<
  typeof EncounterNavigationSourceSchema
>

function serializeSourceValue(value: EncounterNavigationSource): string {
  return [value.source, value.entityId].filter(Boolean).join(':')
}

const parseAsEncounterNavigationSource =
  createParser<EncounterNavigationSource>({
    parse: (queryValue) => {
      const [source, entityId] = queryValue.split(':')
      return (
        EncounterNavigationSourceSchema.safeParse({
          entityId,
          source,
        }).data ?? null
      )
    },
    serialize: serializeSourceValue,
  }).withDefault({ source: 'list' })

export function serializeEncounterNavigationSource(
  baseUrl: string,
  navigationSource: EncounterNavigationSource,
): string {
  const questionMarkIndex = baseUrl.indexOf('?')
  const path =
    questionMarkIndex >= 0 ? baseUrl.slice(0, questionMarkIndex) : baseUrl
  const params = new URLSearchParams(
    questionMarkIndex >= 0 ? baseUrl.slice(questionMarkIndex + 1) : '',
  )

  if (navigationSource.source === 'list') {
    params.delete('source')
  } else {
    params.set('source', serializeSourceValue(navigationSource))
  }

  const search = params.toString()
  return search.length > 0 ? `${path}?${search}` : path
}

export function useEncounterNavigationSource() {
  const [source] = useQueryState('source', parseAsEncounterNavigationSource)
  return source
}

export function useClearEncounterNavigationSource() {
  const navigate = useNavigate()

  return () => {
    const href = serializeEncounterNavigationSource(
      `${window.location.pathname}${window.location.search}`,
      { source: 'list' },
    )
    void navigate(`${href}${window.location.hash}`, { replace: true })
  }
}
